/**
 * Output descriptors — capability registry. Implements docs/DESCRIPTORS.md.
 *
 * The registry exists so a new descriptor class is a contained addition rather than a
 * rewrite. v1 registers `wsh(sortedmulti)` only.
 *
 * FAIL-CLOSED: a descriptor matching no registered class is UNSUPPORTED_DESCRIPTOR and is
 * never parsed on a best-effort basis. Half-support derives some addresses, silently misses
 * the change output, and turns the product's best check into a coin flip.
 */
import * as btc from '@scure/btc-signer';
import { HDKey } from '@scure/bip32';
import { bytesToHex } from '@noble/hashes/utils.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { ripemd160 } from '@noble/hashes/legacy.js';
/**
 * BIP-32 serialization versions, keyed by the SLIP-132 prefix that encodes them.
 *
 * Network used to be decided by `xpub.startsWith('xpub') ? 'mainnet' : 'testnet'`, which is
 * only correct for the two BIP-32 prefixes. `wsh(sortedmulti)` vaults are exactly the wallets
 * that use the P2WSH-multisig prefixes — Coldcard, Sparrow and Electrum all export `Zpub` on
 * mainnet — so the commonest real descriptor for this product's own class was labelled
 * `testnet`. It then derived TESTNET addresses for a MAINNET vault, so every input and the
 * change output came back "not yours", or `fromExtendedKey` threw at derive time with a version
 * mismatch that says nothing about the actual mistake.
 */
const KEY_PREFIXES = {
    // mainnet
    xpub: { network: 'mainnet', private: 0x0488ade4, public: 0x0488b21e },
    ypub: { network: 'mainnet', private: 0x049d7878, public: 0x049d7cb2 }, // p2sh-p2wpkh
    zpub: { network: 'mainnet', private: 0x04b2430c, public: 0x04b24746 }, // p2wpkh
    Ypub: { network: 'mainnet', private: 0x0295b005, public: 0x0295b43f }, // p2sh-p2wsh multisig
    Zpub: { network: 'mainnet', private: 0x02aa7a99, public: 0x02aa7ed3 }, // p2wsh multisig
    // testnet
    tpub: { network: 'testnet', private: 0x04358394, public: 0x043587cf },
    upub: { network: 'testnet', private: 0x044a4e28, public: 0x044a5262 },
    vpub: { network: 'testnet', private: 0x045f18bc, public: 0x045f1cf6 },
    Upub: { network: 'testnet', private: 0x024285b5, public: 0x024289ef },
    Vpub: { network: 'testnet', private: 0x02575048, public: 0x02575483 },
};
function prefixInfo(xpub) {
    const info = KEY_PREFIXES[xpub.slice(0, 4)];
    if (!info)
        throw new DescriptorError('UNKNOWN_KEY_PREFIX', `"${xpub.slice(0, 4)}" is not a recognised extended-key prefix. Refusing to guess the ` +
            `network: guessing wrong derives another chain's addresses and disowns every output.`);
    return info;
}
export class DescriptorError extends Error {
    code;
    constructor(code, message) {
        // Code goes in the message too: it is what shows up in logs and in test matchers.
        super(`${code}: ${message}`);
        this.code = code;
        this.name = 'DescriptorError';
    }
}
/* ── BIP-380 checksum ─────────────────────────────────────────────────────── */
const INPUT_CHARSET = "0123456789()[],'/*abcdefgh@:$%{}IJKLMNOPQRSTUVWXYZ&+-.;<=>?!^_|~ijklmnopqrstuvwxyzABCDEFGH`#\"\\ ";
const CHECKSUM_CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';
const GENERATOR = [0xf5dee51989n, 0xa9fdca3312n, 0x1bab10e32dn, 0x3706b1677an, 0x644d626ffdn];
function polymod(symbols) {
    let chk = 1n;
    for (const value of symbols) {
        const top = chk >> 35n;
        chk = ((chk & 0x7ffffffffn) << 5n) ^ BigInt(value);
        for (let i = 0; i < 5; i++)
            if ((top >> BigInt(i)) & 1n)
                chk ^= GENERATOR[i];
    }
    return chk;
}
function expand(s) {
    const groups = [];
    const symbols = [];
    for (const c of s) {
        const v = INPUT_CHARSET.indexOf(c);
        if (v < 0)
            return null;
        symbols.push(v & 31);
        groups.push(v >> 5);
        if (groups.length === 3) {
            symbols.push(groups[0] * 9 + groups[1] * 3 + groups[2]);
            groups.length = 0;
        }
    }
    if (groups.length === 1)
        symbols.push(groups[0]);
    else if (groups.length === 2)
        symbols.push(groups[0] * 3 + groups[1]);
    return symbols;
}
/** Compute the 8-character BIP-380 checksum for a descriptor payload. */
export function descriptorChecksum(payload) {
    const symbols = expand(payload);
    if (symbols === null)
        throw new DescriptorError('DESCRIPTOR_CHARSET', 'descriptor contains an invalid character');
    const chk = polymod([...symbols, 0, 0, 0, 0, 0, 0, 0, 0]) ^ 1n;
    let out = '';
    for (let i = 0; i < 8; i++)
        out += CHECKSUM_CHARSET[Number((chk >> BigInt(5 * (7 - i))) & 31n)];
    return out;
}
/** Append a correct checksum. Useful for fixtures and for normalising user input. */
export function withChecksum(payload) {
    return `${payload}#${descriptorChecksum(payload)}`;
}
/* ── wsh(sortedmulti) ─────────────────────────────────────────────────────── */
const KEY_RE = /^\[([0-9a-fA-F]{8})((?:\/[^/\]]*)*)\]([a-zA-Z0-9]+)(\/.*)?$/;
/** BIP-32 hardened offset. An unhardened child index must be strictly below this. */
const HARDENED = 0x80000000;
/**
 * One element of an origin path: digits, optionally followed by ONE hardened marker.
 *
 * The old path pattern was `(?:\/\d+'?h?)*` — `'?h?` makes BOTH markers optional and
 * independent, so `48'h` matched and was silently kept. Two wallets shown the same descriptor
 * would disagree about whether that text is even a path, and the origin is what a signing
 * device matches to decide the key is its own.
 */
const PATH_ELEM_RE = /^(\d+)(['h])?$/;
function assertChildIndex(raw, what) {
    const n = Number(raw);
    if (!Number.isSafeInteger(n) || n >= HARDENED)
        throw new DescriptorError('KEY_INDEX_RANGE', `${what} "${raw}" is not a valid unhardened child index — BIP-32 indices run to ` +
            `${HARDENED - 1}, and anything at or above that is the hardened range written wrong`);
    return n;
}
function parseKeyExpr(raw) {
    const m = KEY_RE.exec(raw.trim());
    if (!m)
        throw new DescriptorError('KEY_EXPR_MALFORMED', `key expression is not [fingerprint/path]xpub/...: ${raw.slice(0, 40)}`);
    const [, fp, path, xpub, tail] = m;
    // Validate every origin-path element explicitly rather than trusting the outer regex:
    // a permissive path pattern is how "48'h" got through in the first place.
    for (const elem of (path ?? '').split('/').slice(1)) {
        const pm = PATH_ELEM_RE.exec(elem);
        if (!pm)
            throw new DescriptorError('KEY_ORIGIN_PATH', `origin path element "${elem}" is not a number with at most one hardened marker ` +
                `(' or h); refusing to guess what it means`);
        assertChildIndex(pm[1], 'origin path element');
    }
    prefixInfo(xpub); // network prefix must be known before anything else is derived from it
    let multipath = null;
    let fixedBranch = null;
    if (tail) {
        const mp = /^\/<(\d+);(\d+)>\/\*$/.exec(tail);
        const fixed = /^\/(\d+)\/\*$/.exec(tail);
        if (mp) {
            multipath = [assertChildIndex(mp[1], 'multipath branch'),
                assertChildIndex(mp[2], 'multipath branch')];
            if (multipath[0] === multipath[1])
                throw new DescriptorError('MULTIPATH_COLLAPSED', `<${multipath[0]};${multipath[1]}> puts receive and change on the same branch, so an ` +
                    `incoming payment and a change output become indistinguishable`);
        }
        else if (fixed) {
            fixedBranch = assertChildIndex(fixed[1], 'fixed branch');
        }
        else if (tail !== '/*') {
            throw new DescriptorError('KEY_DERIVATION_UNSUPPORTED', `unsupported derivation suffix "${tail}" — only /<a;b>/* , /n/* and /* are understood`);
        }
    }
    return {
        origin: { fingerprint: fp.toLowerCase(), path: `m${path ?? ''}` },
        xpub: xpub,
        multipath,
        fixedBranch,
    };
}
/** Split on commas that are at paren/bracket depth zero. */
function splitTop(s) {
    const out = [];
    let depth = 0, start = 0;
    for (let i = 0; i < s.length; i++) {
        const c = s[i];
        if (c === '(' || c === '[' || c === '<')
            depth++;
        else if (c === ')' || c === ']' || c === '>')
            depth--;
        else if (c === ',' && depth === 0) {
            out.push(s.slice(start, i));
            start = i + 1;
        }
    }
    out.push(s.slice(start));
    return out;
}
const WSH_SORTEDMULTI_RE = /^wsh\(sortedmulti\((.+)\)\)$/s;
/* ── derivation caches ────────────────────────────────────────────────────── */
/*
 * `isMine` answers "is this script ours?" by deriving every index in the scan window and
 * comparing. It is called once per input and once per output, and it used to redo the entire
 * window every time — including re-parsing each xpub from base58 at every index and re-deriving
 * the branch node at every index.
 *
 * Measured on a 3-in/2-out transaction: 486 ms at lastUsed=0, 1.4 s at lastUsed=46, and
 * 20.9 s at lastUsed=1000 — twenty seconds of frozen UI on the surface where somebody decides
 * whether to sign, for a vault that had merely been used a lot. The UI even tells people to set
 * the window generously, so the advice pointed straight at the slow path.
 *
 * Everything cached here is a pure function of (xpub, network, branch, index), so caching cannot
 * change an answer — only how long it takes to get it. The caps exist because a vault with a
 * very wide window would otherwise pin an unbounded amount of memory; clearing wholesale is fine
 * because a cold cache is only ever slower, never wrong.
 */
const MAX_KEYS = 64;
const MAX_DERIVATIONS = 20_000;
const parsedXpubs = new Map();
const branchNodes = new Map();
function xpubNode(xpub, network) {
    const key = `${network}:${xpub}`;
    let node = parsedXpubs.get(key);
    if (!node) {
        if (parsedXpubs.size > MAX_KEYS)
            parsedXpubs.clear();
        node = HDKey.fromExtendedKey(xpub, prefixInfo(xpub));
        parsedXpubs.set(key, node);
    }
    return node;
}
function branchNode(xpub, network, branch) {
    const key = `${network}:${xpub}:${branch}`;
    let node = branchNodes.get(key);
    if (!node) {
        if (branchNodes.size > MAX_KEYS * 2)
            branchNodes.clear();
        node = xpubNode(xpub, network).deriveChild(branch);
        branchNodes.set(key, node);
    }
    return node;
}
const derivationCache = {
    map: new Map(),
    // `payload` is the descriptor without its checksum, so it pins the key set, m and n exactly.
    key: (d, branch, index) => `${d.payload}|${branch}|${index}`,
    get(d, branch, index) {
        return this.map.get(this.key(d, branch, index));
    },
    set(d, branch, index, v) {
        if (this.map.size > MAX_DERIVATIONS)
            this.map.clear();
        this.map.set(this.key(d, branch, index), v);
        return v;
    },
};
/** Exposed for tests and for callers that want a predictable cold start. */
export function clearDerivationCaches() {
    parsedXpubs.clear();
    branchNodes.clear();
    derivationCache.map.clear();
}
export const wshSortedMulti = {
    id: 'wsh-sortedmulti',
    label: 'Multisig — wsh(sortedmulti)',
    matches(descriptor) {
        return WSH_SORTEDMULTI_RE.test(stripChecksum(descriptor).payload);
    },
    parse(descriptor) {
        const { payload, checksum } = stripChecksum(descriptor);
        if (checksum === null)
            throw new DescriptorError('DESCRIPTOR_CHECKSUM_MISSING', 'descriptor has no checksum; refusing to guess');
        const expected = descriptorChecksum(payload);
        if (checksum !== expected)
            throw new DescriptorError('DESCRIPTOR_CHECKSUM_FAIL', `checksum ${checksum} does not match computed ${expected}`);
        const m = WSH_SORTEDMULTI_RE.exec(payload);
        if (!m)
            throw new DescriptorError('UNSUPPORTED_DESCRIPTOR', 'not a wsh(sortedmulti) descriptor');
        const parts = splitTop(m[1]).map((p) => p.trim());
        // `Number()` accepts 0x2, 2e0, +2 and 2.0 — all coerce to 2. A descriptor is a canonical
        // string that other wallets must parse identically; accepting alternative spellings means
        // two implementations can disagree about the same text. Fail closed: plain digits only.
        if (!/^[1-9]\d*$/.test(parts[0] ?? ''))
            throw new DescriptorError('THRESHOLD_INVALID', `threshold "${parts[0]}" is not written as a plain positive integer`);
        const threshold = Number(parts[0]);
        const keys = parts.slice(1).map(parseKeyExpr);
        if (keys.length < threshold)
            throw new DescriptorError('THRESHOLD_EXCEEDS_KEYS', `threshold ${threshold} exceeds ${keys.length} keys`);
        // Network comes from the SLIP-132 prefix table, not from `startsWith('xpub')`. Different
        // prefixes on the SAME network are fine (they are serialization variants of one key), so
        // the check is on the network each prefix denotes.
        const networks = new Set(keys.map((k) => prefixInfo(k.xpub).network));
        if (networks.size !== 1)
            throw new DescriptorError('MIXED_NETWORKS', 'descriptor mixes mainnet and testnet keys');
        const network = prefixInfo(keys[0].xpub).network;
        const fps = keys.map((k) => k.origin.fingerprint);
        if (new Set(fps).size !== fps.length)
            throw new DescriptorError('DUPLICATE_FINGERPRINT', 'the same master fingerprint appears twice; m-of-n would be illusory');
        /*
         * The fingerprint is attacker-supplied text in the descriptor; the XPUB is the thing that
         * actually signs. Checking only fingerprints let the same xpub appear three times under
         * three different fingerprints and parse as a valid 2-of-3 — one device satisfying the whole
         * policy, which is the exact scenario m-of-n exists to prevent.
         *
         * ⚠️ COMPARE THE KEY, NOT THE ENCODING. This used to compare the xpub STRINGS, and `xpub…`
         * (0x0488b21e) and `Zpub…` (0x02aa7ed3) are two SLIP-132 serialisations of the same key.
         * Both are mainnet so `MIXED_NETWORKS` passed; the fingerprints are chosen text so
         * `DUPLICATE_FINGERPRINT` passed; the strings differ so this passed. The descriptor was
         * accepted and stored, and the failure surfaced much later as a raw `@scure` string —
         * "Multisig: non-uniq pubkey" — escaping through `deriveIntent` and rendered verbatim to the
         * user. The vault saved cleanly and then every PSBT failed with an unexplained library
         * message.
         *
         * The identity of a BIP-32 extended key is its public key and chain code. Serialisation
         * prefix, depth and parent fingerprint are packaging.
         */
        const identity = keys.map((k) => {
            const node = xpubNode(k.xpub, network);
            if (!node.publicKey)
                throw new DescriptorError('DERIVE_FAILED', `could not read the extended key for ${k.origin.fingerprint}`);
            return `${bytesToHex(node.publicKey)}:${bytesToHex(node.chainCode ?? new Uint8Array())}`;
        });
        if (new Set(identity).size !== identity.length)
            throw new DescriptorError('DUPLICATE_XPUB', 'the same extended key appears more than once — possibly under two different SLIP-132 ' +
                'prefixes, which encode the same key. Whatever the fingerprints say, one key would ' +
                'satisfy several slots and the m-of-n threshold would be illusory.');
        return { classId: 'wsh-sortedmulti', m: threshold, n: keys.length, keys, network, checksum, payload };
    },
    derive(d, branch, index) {
        const cached = derivationCache.get(d, branch, index);
        if (cached)
            return cached;
        const net = d.network === 'mainnet' ? btc.NETWORK : btc.TEST_NETWORK;
        /** Which child of each key this scan branch actually derives — see `path` below. */
        const childOf = (k) => k.multipath ? (branch === 0 ? k.multipath[0] : k.multipath[1])
            : k.fixedBranch !== null ? k.fixedBranch
                : null;
        const pubkeys = d.keys.map((k) => {
            /*
             * Which child to derive.
             *
             *   <a;b>/*  multipath — branch 0 takes a, branch 1 takes b
             *   /n/*     FIXED branch n — the scan branch is NOT a child here
             *   /*       bare — the key IS the branch node; only the index is derived
             *
             * `k.multipath ? … : branch` collapsed the last two cases into "derive child `branch`",
             * so a `/5/*` descriptor derived children 0 and 1 — addresses that are NOT this vault's.
             * Every input and the change output came back `mine: false`, giving a permanent
             * CHANGE_NOT_MINE on a correct descriptor, while a genuinely foreign output at 0/x or 1/x
             * would have been labelled "yours".
             */
            const b = childOf(k);
            // The branch node is shared by every index under it, so it is derived once and reused.
            // Re-deriving it per index — and re-parsing the xpub from base58 per index, which is what
            // this used to do — is what made a wide scan window take twenty seconds.
            // A bare `/*` key already IS the branch node, so only the index is derived below it.
            const node = b === null ? xpubNode(k.xpub, d.network) : branchNode(k.xpub, d.network, b);
            const child = node.deriveChild(index);
            if (!child.publicKey)
                throw new DescriptorError('DERIVE_FAILED', `could not derive ${k.origin.fingerprint}`);
            return child.publicKey;
        });
        // sortedmulti = BIP-67 lexicographic ordering of the *derived* pubkeys.
        const sorted = btc._sortPubkeys(pubkeys);
        const ms = btc.p2wsh(btc.p2ms(d.m, sorted), net);
        if (!ms.address || !ms.witnessScript)
            throw new DescriptorError('DERIVE_FAILED', 'p2wsh did not produce an address');
        /*
         * The path is reported with the KEY-CHILD number, not the scan branch.
         *
         * They are the same for the usual `<0;1>` descriptor and differ for every other shape. A
         * `<1;0>` vault derives its receive addresses under key-child 1, and we scanned that as
         * branch 0 — so the panel said `m/0/3` about an address the signing device, Sparrow and
         * Core all call `m/1/3`. Somebody checking "does this path match what my Coldcard shows"
         * gets a mismatch on a correct descriptor, which is the check most likely to be abandoned
         * after it cries wolf once. A `/5/*` vault was worse: every address reported 0/x or 1/x,
         * neither of which exists in that wallet.
         */
        return derivationCache.set(d, branch, index, {
            branch, index, path: `${childOf(d.keys[0]) ?? branch}/${index}`,
            script: ms.script, address: ms.address, witnessScript: ms.witnessScript,
            // For P2WSH the script code IS the witness script.
            scriptCode: ms.witnessScript,
            sortedPubkeys: sorted, pubkeys,
        });
    },
};
function stripChecksum(descriptor) {
    const i = descriptor.lastIndexOf('#');
    if (i < 0)
        return { payload: descriptor.trim(), checksum: null };
    return { payload: descriptor.slice(0, i).trim(), checksum: descriptor.slice(i + 1).trim() };
}
/* ── registry ─────────────────────────────────────────────────────────────── */
/** Shared wallets and single-signature wallets. See docs/DESCRIPTORS.md §4 for the tiers. */
/* ── single-signature ──────────────────────────────────────────────────────── */
const WPKH_RE = /^wpkh\((.+)\)$/s;
const SH_WPKH_RE = /^sh\(wpkh\((.+)\)\)$/s;
/**
 * One key, one signature. `wpkh(...)` and its wrapped form `sh(wpkh(...))`.
 *
 * ⚠️ A SINGLE-SIGNATURE WALLET IS NOT A LESSER CASE OF A SHARED ONE, and its holder is not a
 * lesser user. Plenty of companies hold bitcoin in a wallet one person can spend from, and that
 * is precisely the arrangement most worth reviewing carefully before approving, because there is
 * no second person to catch a mistake. Refusing to read those wallets meant the people with the
 * least protection got the least help.
 *
 * Everything the risk engine does still applies: whose coins these are, where the change lands,
 * whether the fee is sane, what the signature commits to. The only difference is that the
 * threshold is 1 of 1, so the policy checks pass trivially and say so rather than staying silent.
 *
 * NOT INCLUDED, deliberately, and named rather than left to be discovered:
 *   · `tr(...)` — Taproot needs BIP-341 sighashes and Schnorr verification, neither of which
 *     `psbt.ts` implements. Accepting it would mean deriving addresses correctly and then
 *     failing to verify any signature, so a threshold would silently never be met.
 *   · `pkh(...)` — pre-segwit, whose signatures use a different preimage again.
 * Both are refused by name in `explain.ts` rather than falling through to a generic error.
 */
function singleSigClass(id, label, re, build) {
    return {
        id,
        label,
        matches(descriptor) {
            return re.test(stripChecksum(descriptor).payload);
        },
        parse(descriptor) {
            const { payload, checksum } = stripChecksum(descriptor);
            if (checksum === null)
                throw new DescriptorError('DESCRIPTOR_CHECKSUM_MISSING', 'descriptor has no checksum; refusing to guess');
            const expected = descriptorChecksum(payload);
            if (checksum !== expected)
                throw new DescriptorError('DESCRIPTOR_CHECKSUM_FAIL', `checksum ${checksum} does not match computed ${expected}`);
            const m = re.exec(payload);
            if (!m)
                throw new DescriptorError('UNSUPPORTED_DESCRIPTOR', `not a ${id} descriptor`);
            // One key expression, and it must not be a list: `wpkh(a,b)` is not a thing, and quietly
            // taking the first of two would be a descriptor that means something else entirely.
            const parts = splitTop(m[1]).map((x) => x.trim());
            if (parts.length !== 1)
                throw new DescriptorError('UNSUPPORTED_DESCRIPTOR', `${id} takes exactly one key, got ${parts.length}`);
            const key = parseKeyExpr(parts[0]);
            const network = prefixInfo(key.xpub).network;
            return { classId: id, m: 1, n: 1, keys: [key], network, checksum, payload };
        },
        derive(d, branch, index) {
            const cached = derivationCache.get(d, branch, index);
            if (cached)
                return cached;
            const net = d.network === 'mainnet' ? btc.NETWORK : btc.TEST_NETWORK;
            const k = d.keys[0];
            const b = k.multipath ? (branch === 0 ? k.multipath[0] : k.multipath[1])
                : k.fixedBranch !== null ? k.fixedBranch
                    : null;
            const node = b === null ? xpubNode(k.xpub, d.network) : branchNode(k.xpub, d.network, b);
            const child = node.deriveChild(index);
            if (!child.publicKey)
                throw new DescriptorError('DERIVE_FAILED', `could not derive ${k.origin.fingerprint}`);
            const pay = build(child.publicKey, net);
            if (!pay.script || !pay.address)
                throw new DescriptorError('DERIVE_FAILED', `${id} did not produce an address`);
            /*
             * The BIP-143 script code for a single-key input: OP_DUP OP_HASH160 <20-byte key hash>
             * OP_EQUALVERIFY OP_CHECKSIG — the P2PKH script, synthesised, with NO length prefix.
             * Measured against `preimageWitnessV0` rather than assumed: the length-prefixed form
             * verifies nothing, and does so silently.
             */
            const h160 = ripemd160(sha256(child.publicKey));
            const scriptCode = new Uint8Array([0x76, 0xa9, 0x14, ...h160, 0x88, 0xac]);
            return derivationCache.set(d, branch, index, {
                branch, index, path: `${b ?? branch}/${index}`,
                script: pay.script, address: pay.address,
                // There is no witness script for a single-key input; the script code is what is signed.
                witnessScript: scriptCode,
                scriptCode,
                sortedPubkeys: [child.publicKey], pubkeys: [child.publicKey],
            });
        },
    };
}
export const wpkhSingle = singleSigClass('wpkh', 'Single signature — wpkh()', WPKH_RE, (pubkey, net) => btc.p2wpkh(pubkey, net));
export const shWpkhSingle = singleSigClass('sh-wpkh', 'Single signature — sh(wpkh())', SH_WPKH_RE, (pubkey, net) => btc.p2sh(btc.p2wpkh(pubkey, net), net));
/**
 * ONE KEY, ONE SIGNATURE. The wallet shapes Solo is for.
 *
 * Solo is the single-signature product: one person holds the key, so one person reads the
 * payment and approves it, and there is no room, no invitation and no coordinator anywhere in
 * the picture.
 */
export const SINGLE_SIG_CLASSES = [wpkhSingle, shWpkhSingle];
/**
 * SEVERAL KEYS, A THRESHOLD. The wallet shapes Org is for.
 *
 * A shared wallet needs several people to approve, which needs somewhere for them to do it —
 * scoped invitations, an approval track, a hash-chained record of who approved what. That is
 * Org, and it is a different product surface rather than a bigger version of this one.
 */
export const MULTISIG_CLASSES = [wshSortedMulti];
/**
 * Every class this engine can derive from.
 *
 * ⚠️ This is NOT the gate. `findMine` and `derive` resolve a class by `classId` against this
 * list, and they must succeed for anything that has already parsed — narrowing it here would
 * mean a descriptor that parsed cleanly and then could not derive an address, which is a far
 * worse failure than a refusal at the door. The gate is `allowed` on `parseDescriptor`, applied
 * once, at the only moment a human is holding the descriptor and can act on the answer.
 */
export const REGISTRY = [
    ...MULTISIG_CLASSES, ...SINGLE_SIG_CLASSES,
];
/**
 * Resolve a descriptor to its class. Throws UNSUPPORTED_DESCRIPTOR rather than attempting a
 * partial parse — see the fail-closed rule at the top of this file.
 *
 * `allowed` narrows the search, which is how Solo and Org are two products rather than one with
 * a setting: Solo passes `SINGLE_SIG_CLASSES`, Org passes `MULTISIG_CLASSES`. A shape outside
 * the given set is refused exactly as an unknown one is — `explain.ts` is what turns that into a
 * sentence naming the other product.
 */
export function resolveClass(descriptor, allowed = REGISTRY) {
    const cls = allowed.find((c) => c.matches(descriptor));
    if (!cls)
        throw new DescriptorError('UNSUPPORTED_DESCRIPTOR', 'no registered descriptor class matches; refusing to parse on a best-effort basis');
    return cls;
}
export function parseDescriptor(descriptor, allowed = REGISTRY) {
    return resolveClass(descriptor, allowed).parse(descriptor);
}
/**
 * The ownership oracle. Returns the derivation path if the script belongs to this
 * descriptor, or null.
 *
 * Scans to `lastUsed[branch] + gapLimit`, never a flat 0..gapLimit.
 */
/**
 * The largest scan ceiling this function will accept, per branch.
 *
 * `findMine` derives EVERY index from 0 to `lastUsed + gapLimit`, on both branches, for every
 * script in the transaction. `derivation-cache.test.ts` measured that cold walk at 1.4 s for a
 * 3-in/2-out transaction at a ceiling of 66, and 20.9 s at a ceiling of ~1000 — and the cache
 * only ever makes the SECOND scan cheap, never the first.
 *
 * So an oversized window is not "slow", it is a frozen review screen on the one page where
 * somebody decides whether to sign, and `gapLimit: 1e9` cleared every check below it: it is a
 * non-negative integer. 500 sits far above anything a real wallet asks for — BIP-44 says 20,
 * and Sparrow, Electrum and Coldcard all ship 20 — and below the 1000 this repo has already
 * measured as unusable.
 */
export const MAX_SCAN_CEILING = 500;
/**
 * The branches `findMine` actually walks. `assertScanWindow` bounds these and only these, so
 * the guard and the loop cannot drift apart: a `lastUsed` entry for a branch the scan never
 * visits costs nothing and must not be refused.
 */
const SCAN_BRANCHES = [0, 1];
/**
 * Validate a scan window, loudly.
 *
 * `ceiling = lastUsed + gapLimit`. With `gapLimit: NaN` the comparison `i <= NaN` is false, the
 * loop never runs, and `isMine` returns null for every script the vault owns — every input
 * "not yours", the change output "not yours", with NO error anywhere. `NaN` is exactly what a
 * blank settings field or `parseInt('')` produces. A negative gap limit pushes the ceiling below
 * an index the window explicitly says has been used.
 *
 * Silently answering "not yours" is the worst possible failure for this function, so it throws.
 *
 * ⚠️ The upper bound is checked on the CEILING, not on `gapLimit` alone. `lastUsed: { 0: 1e9 }`
 * with a gap limit of 20 costs exactly what `gapLimit: 1e9` costs, so a guard that only reads
 * `gapLimit` refuses one spelling of the hang and waves the other through. The branches walked
 * here are the branches `findMine` walks, resolved the same way — `?? 0` included — because a
 * bound computed over a different set than the loop uses is a bound with a gap in it.
 */
export function assertScanWindow(window) {
    if (!Number.isInteger(window.gapLimit) || window.gapLimit < 0)
        throw new DescriptorError('BAD_SCAN_WINDOW', `gapLimit must be a non-negative integer, got ${String(window.gapLimit)} — a bad gap ` +
            `limit makes isMine answer "not yours" for every script the vault owns, with no error`);
    for (const [branch, last] of Object.entries(window.lastUsed)) {
        if (!Number.isInteger(last) || last < 0)
            throw new DescriptorError('BAD_SCAN_WINDOW', `lastUsed[${branch}] must be a non-negative integer, got ${String(last)}`);
    }
    for (const branch of SCAN_BRANCHES) {
        const ceiling = (window.lastUsed[branch] ?? 0) + window.gapLimit;
        if (ceiling > MAX_SCAN_CEILING)
            throw new DescriptorError('BAD_SCAN_WINDOW', `branch ${branch} would scan 0..${ceiling}, above the ${MAX_SCAN_CEILING} ceiling ` +
                `(lastUsed ${window.lastUsed[branch] ?? 0} + gapLimit ${window.gapLimit}) — a window ` +
                `this wide does not answer slowly, it stops the review screen from answering at all`);
    }
}
export function findMine(d, script, window) {
    assertScanWindow(window);
    const cls = REGISTRY.find((c) => c.id === d.classId);
    if (!cls)
        throw new DescriptorError('UNSUPPORTED_DESCRIPTOR', `no class for ${d.classId}`);
    const target = bytesToHex(script);
    for (const branch of SCAN_BRANCHES) {
        const ceiling = (window.lastUsed[branch] ?? 0) + window.gapLimit;
        for (let i = 0; i <= ceiling; i++) {
            const derived = cls.derive(d, branch, i);
            if (bytesToHex(derived.script) === target)
                return derived;
        }
    }
    return null;
}
/**
 * An address, back to the script it pays. The mirror of `encodeAddress` in `intent.ts`.
 *
 * Throws on anything that is not a valid address for THIS network — a mainnet address handed to
 * a testnet wallet is refused here, by name, rather than silently failing to match anything.
 */
export function decodeAddress(address, network) {
    const net = network === 'mainnet' ? btc.NETWORK : btc.TEST_NETWORK;
    try {
        return btc.OutScript.encode(btc.Address(net).decode(address.trim()));
    }
    catch (e) {
        throw new DescriptorError('BAD_ADDRESS', `"${address.trim()}" is not a valid ${network} address: ${e.message}`);
    }
}
/**
 * Which of this wallet's addresses is this? — the question a key holder can actually answer.
 *
 * Proof of control needs to know WHERE a signature was made: a wallet signs with the key for one
 * address, and the verifier compares against that one. The first surface asked for the branch
 * and index. Nobody who is not a wallet developer knows those numbers, but everybody knows the
 * address — Sparrow, Core and Electrum all show it on the signing screen — so this takes the
 * address and finds the coordinates with the same scan `isMine` uses.
 *
 * Null means "not within the scan window", which is a different answer from "not yours": the
 * caller should say which, and point at the window setting.
 */
export function findByAddress(d, address, window) {
    return findMine(d, decodeAddress(address, d.network), window);
}
/**
 * The same question, answered with the derivation path only.
 *
 * Callers that need to derive again should use `findMine`: the returned `Derived` carries the
 * SCAN coordinates as well as the reported path, and for anything other than a `<0;1>`
 * descriptor those are different numbers. Re-deriving from the reported path would silently
 * produce a different address.
 */
export function isMine(d, script, window) {
    return findMine(d, script, window)?.path ?? null;
}
//# sourceMappingURL=descriptors.js.map